import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { usePublicMenu } from '@/features/food/api'
import { MenuSection } from '@/features/food/components'
import { usePublicDrinks } from '@/features/tables/api'
import { OrderOnlyDrinks } from '@/features/tables/OrderOnlyDrinks'
import { TablesSection } from '@/features/tables/components'

import { useEventContent, usePublicEvent } from '../api'
import { RubricPage } from '../components/RubricPage'

/**
 * La carte : plats, boissons, et réservation de table.
 *
 * Les trois vivaient sur la page d'accueil, où ils pesaient à eux seuls la
 * moitié de la hauteur. Ils forment ici un tout cohérent : on choisit ce qu'on
 * mange, ce qu'on boit, et la table qui va avec — c'est le même geste.
 */
export function MenuPage() {
  const { t, i18n } = useTranslation()

  return (
    <RubricPage title={t('rubrics.menu.title')} intro={t('rubrics.menu.body')}>
      {(event) => <MenuPageBody slug={event.slug} locale={i18n.language} />}
    </RubricPage>
  )
}

function MenuPageBody({ slug, locale }: { slug: string; locale: string }) {
  const menuQuery = usePublicMenu(slug)
  const navigate = useNavigate()
  const drinksQuery = usePublicDrinks(slug)
  const eventQuery = usePublicEvent()
  const content = useEventContent(slug)

  const settings = eventQuery.data?.settings ?? {}
  // Le bloc éditorial des tables : ce que l'équipe veut en dire, et sa photo.
  const intro = (content.data ?? []).find((row) => row.key === 'tables.intro')

  return (
    <>
      {menuQuery.data ? <MenuSection menu={menuQuery.data} locale={locale} /> : null}
      {/* Absent = visible pour les deux : masquer les tables composées par
          l'équipe parce qu'un réglage n'est pas encore chargé effacerait leur
          travail à chaque hoquet du réseau. */}
      {drinksQuery.data ? (
        <TablesSection
          catalog={drinksQuery.data}
          locale={locale}
          showCatalog={settings['tables.show_catalog'] !== false}
          allowCustomization={settings['tables.allow_customization'] !== false}
          intro={
            intro
              ? { title: intro.title, body: intro.body, image_url: intro.image_url }
              : undefined
          }
        />
      ) : null}

      {/* Les bouteilles hors table, sous les formules : on les découvre après
          avoir vu ce qui est compris, pas avant. La section se retire
          d'elle-même si aucune n'est marquée « sur commande ». */}
      {drinksQuery.data ? (
        <OrderOnlyDrinks
          drinks={drinksQuery.data.drinks}
          locale={locale}
          onOrder={(selection) =>
            navigate(
              `/billets?drinks=${selection
                .map((row) => `${row.drink_item_id}:${row.quantity}`)
                .join(',')}`,
            )
          }
        />
      ) : null}
    </>
  )
}
